import { Popover } from 'antd'
import clsx from 'clsx'
import { type FC, useState } from 'react'
import { FaPen } from 'react-icons/fa6'

import { StatusTag } from '@/components/elements'
import {
  type ShipmentDeliveryStatusType,
  SHIPMENT_DELIVERY_STATUS_TYPE,
  SHIPMENT_DELIVERY_STATUS_TYPE_LABEL
} from '@/libs/api/types'

import {
  SHIPMENT_DELIVERY_STATUS_COLOR_MAP,
  type ShipmentDeliveryStatusColor
} from '../shipment-delivery-status-color'

type Variant = 'clickable-tag' | 'tag-with-edit-icon'

type Props = {
  deliveryStatus: ShipmentDeliveryStatusType
  onChange: (status: ShipmentDeliveryStatusType) => Promise<void>
  variant?: Variant
  /** タグサイズ。読み取り専用タグと揃え、編集可能でも表示が大きくならないようにする */
  size?: 'default' | 'small'
  /** ポップオーバーの開閉を親に通知する（矢羽根フローティング内で開いている間パネルを閉じさせない用途） */
  onOpenChange?: (open: boolean) => void
  /**
   * 置かれた枠の幅に合わせてタグを縮め、入りきらないラベルは末尾を省略表示（「受…」）にする。
   * 列幅を利用者が変えられる一覧で使う（#3690 VOC_PJ-577）。未指定なら最小幅を保つ従来の表示
   */
  isFitToWidth?: boolean
}

const allStatuses = Object.values(SHIPMENT_DELIVERY_STATUS_TYPE)

const DOT_COLOR_CLASS: Record<ShipmentDeliveryStatusColor, string> = {
  primary: 'bg-primary',
  secondary: 'bg-secondary',
  info: 'bg-info',
  green: 'bg-green',
  yellow: 'bg-yellow',
  red: 'bg-red',
  'orange-5': 'bg-orange-5',
  'orange-6': 'bg-orange-6',
  'orange-7': 'bg-orange-7',
  'blue-5': 'bg-blue-5',
  'green-7': 'bg-green-7',
  'green-8': 'bg-green-8'
}

/**
 * 配送ステータスの編集可能Tag（配送ステータス11段階用）
 */
export const EditableDeliveryStatusTag: FC<Props> = ({
  deliveryStatus,
  onChange,
  variant = 'clickable-tag',
  size = 'default',
  onOpenChange,
  isFitToWidth = false
}) => {
  const [open, setOpen] = useState(false)
  const [updating, setUpdating] = useState(false)
  const fixedMinWidthClass = size === 'small' ? 'min-w-[84px]' : 'min-w-[136px]'
  const fitMaxWidthClass = size === 'small' ? 'max-w-[84px]' : 'max-w-[136px]'
  // 縮める場合は最小幅の代わりに、従来の最小幅を上限として枠いっぱいに広げる
  // （列が広ければ従来と同じ大きさ、狭ければ縮んで省略表示になる）
  const minWidthClass = isFitToWidth
    ? clsx('w-full truncate align-middle', fitMaxWidthClass)
    : fixedMinWidthClass

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    onOpenChange?.(next)
  }

  const handleSelect = async (status: ShipmentDeliveryStatusType) => {
    if (status === deliveryStatus) {
      handleOpenChange(false)
      return
    }
    setUpdating(true)
    try {
      await onChange(status)
      handleOpenChange(false)
    } finally {
      setUpdating(false)
    }
  }

  const DeliveryStatusTag = (
    <StatusTag
      className={minWidthClass}
      size={size}
      color={SHIPMENT_DELIVERY_STATUS_COLOR_MAP[deliveryStatus]}
    >
      {SHIPMENT_DELIVERY_STATUS_TYPE_LABEL[deliveryStatus]}
    </StatusTag>
  )

  const popoverContent = (
    <div className="flex flex-col gap-0.5">
      {allStatuses.map((status) => {
        const color = SHIPMENT_DELIVERY_STATUS_COLOR_MAP[status]
        return (
          <button
            key={status}
            type="button"
            className={clsx(
              'flex items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-neutral-2',
              status === deliveryStatus && 'bg-neutral-2 font-medium',
              updating && 'pointer-events-none opacity-50'
            )}
            onClick={() => handleSelect(status)}
          >
            <span
              className={clsx(
                'inline-block h-2.5 w-2.5 rounded-full',
                DOT_COLOR_CLASS[color]
              )}
            />
            {SHIPMENT_DELIVERY_STATUS_TYPE_LABEL[status]}
          </button>
        )
      })}
    </div>
  )

  if (variant === 'tag-with-edit-icon') {
    return (
      <div className="flex items-center gap-1">
        {DeliveryStatusTag}
        <Popover
          content={popoverContent}
          trigger="click"
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomLeft"
          arrow={false}
        >
          <button
            type="button"
            className="hover:text-neutral-8 flex h-5 w-5 items-center justify-center rounded text-neutral-6 hover:bg-neutral-3"
          >
            <FaPen size={10} />
          </button>
        </Popover>
      </div>
    )
  }

  // variant === 'clickable-tag'
  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomLeft"
      arrow={false}
    >
      <div className="cursor-pointer">
        <StatusTag
          className={clsx(minWidthClass, 'transition-opacity hover:opacity-80')}
          size={size}
          // 省略表示になっても、ポインタを乗せれば全文が読めるようにする
          title={
            isFitToWidth
              ? SHIPMENT_DELIVERY_STATUS_TYPE_LABEL[deliveryStatus]
              : undefined
          }
          color={SHIPMENT_DELIVERY_STATUS_COLOR_MAP[deliveryStatus]}
        >
          {SHIPMENT_DELIVERY_STATUS_TYPE_LABEL[deliveryStatus]}
        </StatusTag>
      </div>
    </Popover>
  )
}
